import Phaser from 'phaser';
import { animalAssetKeys, AnimalSprite, DEFAULT_ANIMAL } from '../art/AnimalSprite';
import { artScale } from '../art/artScale';
import {
  ensureEnvironmentTextures,
  farLayerYFor,
  NEAR_TILE_HEIGHT,
  SKY_TEXTURE_HEIGHT,
} from '../art/environments';
import { queueArt } from '../art/loadArt';
import { animalById, bodyTintFor, isAnimalId } from '../data/animals';
import type { AnimalId } from '../data/animals';
import { backgroundLayerKey } from '../data/themes';
import { audioOf } from '../systems/audioRegistry';
import { setDevState } from '../systems/devQuery';
import type { SaveManager } from '../systems/SaveManager';
import { arcHeightFor, arcPoint, jumpDurationFor, PLATFORM_SURFACE_INSET } from '../systems/tower';
import type { Point } from '../systems/tower';
import { DESIGN_WIDTH, FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import type { BackgroundTheme } from '../types';
import { showLoadingBar } from './ui/loadingBar';
import { pressButton, styleButton } from './ui/menuButton';
import { tideOpenFor } from './TideStart';

/** The title stands in the same sunset the first levels are set in. */
const TITLE_ENV: BackgroundTheme = 'sunset';

// Wide stage first; the tall stage stacks the same pieces down the screen.
const LOGO_Y = IS_PORTRAIT ? Math.round(GAME_HEIGHT * 0.17) : 140;
const SUBTITLE_Y = IS_PORTRAIT ? Math.round(GAME_HEIGHT * 0.27) : 282;
const MENU_Y = IS_PORTRAIT ? Math.round(GAME_HEIGHT * 0.74) : 664;

const FLOE_SCALE = IS_PORTRAIT ? 0.66 : 0.8;
const HERO_SCALE = 1.1;

/** Three floes to hop between: two low at the sides, one higher in the middle. */
const FLOES: ReadonlyArray<{ readonly x: number; readonly y: number; readonly key: string }> =
  IS_PORTRAIT
    ? [
        { x: 150, y: Math.round(GAME_HEIGHT * 0.6), key: 'platforms/ice-floe/platform_1' },
        { x: 360, y: Math.round(GAME_HEIGHT * 0.54), key: 'platforms/start' },
        { x: 570, y: Math.round(GAME_HEIGHT * 0.6), key: 'platforms/ice-floe/platform_3' },
      ]
    : [
        { x: 360, y: 596, key: 'platforms/ice-floe/platform_1' },
        { x: 640, y: 512, key: 'platforms/start' },
        { x: 920, y: 596, key: 'platforms/ice-floe/platform_3' },
      ];

/** Middle, right, middle, left: a loop that always hops to a neighbour. */
const HOP_ROUTE: readonly number[] = [1, 2, 1, 0];
const HOP_PAUSE_MS = 1100;
const CROUCH_MS = 90;

export class Title extends Phaser.Scene {
  private animalId: AnimalId = DEFAULT_ANIMAL;
  private hero: AnimalSprite | undefined;
  private spots: Point[] = [];
  private hopStep = 0;
  private buttons: Phaser.GameObjects.Text[] = [];
  private actions: Array<() => void> = [];
  private focused = 0;

  constructor() {
    super('Title');
  }

  /**
   * The hopping animal needs its crouch, jump, fall and land frames, which the
   * boot load only fetches for the starting animal. A returning player's own
   * animal is loaded here, the same way Game loads a level's.
   */
  preload(): void {
    this.animalId = this.chosenAnimal();
    queueArt(this, animalAssetKeys(this.animalId));
    showLoadingBar(this);
  }

  create(): void {
    setDevState({ scene: 'Title' });
    // Scene instances are reused, so nothing may carry over from a last visit.
    this.hero = undefined;
    this.spots = [];
    this.hopStep = 0;
    this.buttons = [];
    this.actions = [];
    this.focused = 0;

    this.cameras.main.setBackgroundColor(PALETTE.sky);
    this.drawBackdrop();

    // The real logo, not the word set in the UI font: it carries the icy
    // lettering and the penguin peeking over the J that the text cannot.
    const logo = this.add
      .image(GAME_WIDTH / 2, LOGO_Y, 'ui/logo')
      .setOrigin(0.5)
      // The logo is drawn 800 wide; on the tall stage it shrinks to fit.
      .setScale(artScale(this, 'ui/logo') * (IS_PORTRAIT ? (GAME_WIDTH - 40) / 800 : 1));
    if (!this.reducedMotion()) {
      this.tweens.add({
        targets: logo,
        y: LOGO_Y - 10,
        duration: 2200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.add
      .text(GAME_WIDTH / 2, SUBTITLE_Y, 'A typing adventure in Antarctica', {
        fontFamily: FONT_FAMILY,
        fontSize: '30px',
        color: PALETTE.ice,
        stroke: PALETTE.ink,
        strokeThickness: 5,
      })
      .setOrigin(0.5);

    this.drawFloes();
    this.drawMenu();
    this.installInput();
    this.addDevShortcuts();

    if (!this.reducedMotion()) this.time.delayedCall(HOP_PAUSE_MS, () => this.hop());
  }

  /* ------------------------------------------------------------ the hero */

  /** The active player's animal, or the starting one before anyone has played. */
  private chosenAnimal(): AnimalId {
    const save = this.registry.get('save') as SaveManager | undefined;
    const selected = save?.activeProfile()?.selectedAnimal;
    return selected !== undefined && isAnimalId(selected) ? selected : DEFAULT_ANIMAL;
  }

  private drawFloes(): void {
    FLOES.forEach((floe) => {
      this.add.image(floe.x, floe.y, floe.key).setScale(FLOE_SCALE * artScale(this, floe.key));
      // The surface is a fixed inset above the art's centre on every platform
      // (docs/ART_SPEC.md), so the animal's feet land the same on each.
      const surface = floe.y - (56 / 2 - PLATFORM_SURFACE_INSET) * FLOE_SCALE;
      this.spots.push({ x: floe.x, y: surface });
    });

    const start = this.spots[HOP_ROUTE[0] ?? 1];
    if (start === undefined) return;

    const animal = animalById(this.animalId);
    const save = this.registry.get('save') as SaveManager | undefined;
    const swatch = save?.activeProfile()?.colorPerAnimal[this.animalId];
    const tint = animal === undefined ? null : bodyTintFor(animal, swatch);

    this.hero = new AnimalSprite(this, start.x, start.y, this.animalId, tint);
    this.hero.setDisplayScale(HERO_SCALE).setDepth(5);
    this.hero.setAnimalState('idle');
  }

  /**
   * One hop along the route, then a pause, then the next.
   *
   * Uses the game's own arc and jump timing (src/systems/tower.ts), so the
   * title shows exactly the jump the player is about to make.
   */
  private hop(): void {
    const hero = this.hero;
    if (hero === undefined || !hero.active) return;

    const from = this.spots[HOP_ROUTE[this.hopStep % HOP_ROUTE.length] ?? 1];
    this.hopStep += 1;
    const to = this.spots[HOP_ROUTE[this.hopStep % HOP_ROUTE.length] ?? 1];
    if (from === undefined || to === undefined) return;

    hero.setAnimalState('crouch');
    this.time.delayedCall(CROUCH_MS, () => {
      if (!hero.active) return;
      hero.setAnimalState('jump');
      const progress = { t: 0 };
      let falling = false;
      this.tweens.add({
        targets: progress,
        t: 1,
        duration: jumpDurationFor(1) * 1.2,
        ease: 'Linear',
        onUpdate: () => {
          const point = arcPoint(from, to, progress.t, arcHeightFor(1) * 1.4);
          hero.setPosition(point.x, point.y);
          if (!falling && progress.t > 0.55) {
            falling = true;
            hero.setAnimalState('fall');
          }
        },
        onComplete: () => {
          hero.setPosition(to.x, to.y);
          hero.setAnimalState('land');
          this.time.delayedCall(160, () => hero.active && hero.setAnimalState('idle'));
          this.time.delayedCall(HOP_PAUSE_MS, () => this.hop());
        },
      });
    });
  }

  /* ------------------------------------------------------------- the menu */

  private drawMenu(): void {
    const save = this.registry.get('save') as SaveManager | undefined;
    const items: Array<readonly [string, () => void]> = [
      ['Play', () => this.scene.start('ProfileSelect')],
    ];
    // Beside Play once the last player has opened it (finished level 5).
    if (tideOpenFor(save)) items.push(['Tide Run', () => this.scene.start('TideStart')]);
    // The grown-up screen asks its own maths question before showing anything.
    items.push(['Grown-ups', () => this.scene.start('ParentStats')]);
    const spacing = items.length > 2 ? 260 : 240;
    items.forEach(([label, action], index) => {
      const button = this.add
        .text(
          IS_PORTRAIT
            ? GAME_WIDTH / 2
            : GAME_WIDTH / 2 + (index - (items.length - 1) / 2) * spacing,
          IS_PORTRAIT ? MENU_Y + index * 110 : MENU_Y,
          label,
          {
            fontFamily: FONT_FAMILY,
            fontSize: index === items.length - 1 ? '26px' : '34px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 22, y: 10 },
          },
        )
        .setOrigin(0.5)
        .setDepth(10)
        .setInteractive({ useHandCursor: true });
      if (IS_PORTRAIT) button.setFixedSize(GAME_WIDTH - 200, 0).setAlign('center');
      button.on('pointerover', () => this.focus(index));
      button.on('pointerup', () => pressButton(button, action));
      this.buttons.push(button);
      this.actions.push(action);
    });
    // Play starts focused, so Enter still starts the game as it always has.
    this.focus(0);
  }

  private focus(index: number): void {
    this.focused = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private installInput(): void {
    // The first real key press is the only moment a browser will let an
    // AudioContext start, so the menus take their chance here.
    this.input.keyboard?.once('keydown', () => {
      const audio = audioOf(this);
      audio?.unlock();
      audio?.startMusic(TITLE_ENV);
    });

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const count = this.buttons.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        this.focus((this.focused + count - 1) % count);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        this.focus((this.focused + 1) % count);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = this.buttons[this.focused];
        const action = this.actions[this.focused];
        if (button !== undefined && action !== undefined) pressButton(button, action);
      }
    });
  }

  /** Dev builds keep their number-key shortcuts straight into a level. */
  private addDevShortcuts(): void {
    if (!import.meta.env.DEV) return;

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 12,
        'dev: 1 = level 1    3 = level 30    or add ?level=N',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '15px',
          color: PALETTE.ice,
          stroke: PALETTE.ink,
          strokeThickness: 4,
        },
      )
      .setOrigin(0.5)
      .setAlpha(0.75);

    this.input.keyboard?.once('keydown-ONE', () => this.scene.start('Game', { level: 1 }));
    this.input.keyboard?.once('keydown-THREE', () => this.scene.start('Game', { level: 30 }));
  }

  private reducedMotion(): boolean {
    return this.registry.get('reducedMotion') === true;
  }

  /**
   * The real sunset environment: the same three layers the first ten levels
   * climb through, so the title and the first climb agree with each other.
   */
  private drawBackdrop(): void {
    ensureEnvironmentTextures(this, TITLE_ENV);
    const sky = this.add
      .image(0, GAME_HEIGHT - SKY_TEXTURE_HEIGHT, backgroundLayerKey(TITLE_ENV, 'sky'))
      .setOrigin(0, 0);
    // The tall stage is centred on the wide art, so its middle is what shows.
    const shift = IS_PORTRAIT ? (GAME_WIDTH - DESIGN_WIDTH) / 2 : 0;
    if (IS_PORTRAIT) {
      // Scaled evenly to cover (never stretched), anchored to the horizon.
      const scale = Math.max(GAME_WIDTH / sky.width, GAME_HEIGHT / sky.height);
      sky
        .setScale(scale)
        .setPosition((GAME_WIDTH - sky.width * scale) / 2, GAME_HEIGHT - sky.height * scale);
    }
    this.add
      .image(shift, farLayerYFor(TITLE_ENV), backgroundLayerKey(TITLE_ENV, 'far'))
      .setOrigin(0, 0);
    this.add
      .image(shift, GAME_HEIGHT - NEAR_TILE_HEIGHT, backgroundLayerKey(TITLE_ENV, 'near'))
      .setOrigin(0, 0);
  }
}
